import React, { useState, useRef } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import {
  AlignRight,
  AlignCenter,
  AlignLeft,
  Trash2,
  ArrowUp,
  ArrowDown,
  Move,
} from 'lucide-react';

const CustomImageNodeView = (props) => {
  const { node, updateAttributes, deleteNode, selected, editor, getPos } = props;
  const { src, alt, align = 'center', width = '50%' } = node.attrs;
  const [isHovered, setIsHovered] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef(null);

  const getImagePos = () => {
    try {
      return typeof getPos === 'function' ? getPos() : null;
    } catch {
      return null;
    }
  };

  /**
   * جابه‌جایی بین پاراگراف‌ها با دکمه‌های بالا / پایین
   */
  const moveImage = (direction) => {
    if (!editor) return;
    const { state, view } = editor;

    const imagePos = getImagePos();
    if (imagePos == null) return;
    const imageNode = state.doc.nodeAt(imagePos);
    if (!imageNode || imageNode.type.name !== 'image') return;

    const $img = state.doc.resolve(imagePos);
    const currentBlockIndex = $img.index(0);
    const doc = state.doc;

    if (direction === 'up') {
      if (currentBlockIndex === 0) return;

      let prevBlockPos = 0;
      for (let i = 0; i < currentBlockIndex - 1; i++) {
        prevBlockPos += doc.child(i).nodeSize;
      }
      const insertPos = prevBlockPos + 1;

      const tr = state.tr;
      tr.delete(imagePos, imagePos + imageNode.nodeSize);
      tr.insert(insertPos, imageNode);
      view.dispatch(tr.scrollIntoView());
    } else {
      if (currentBlockIndex < doc.childCount - 1) {
        let nextBlockPos = 0;
        for (let i = 0; i < currentBlockIndex + 1; i++) {
          nextBlockPos += doc.child(i).nodeSize;
        }
        const insertPos = nextBlockPos + 1;

        const tr = state.tr;
        tr.delete(imagePos, imagePos + imageNode.nodeSize);
        const adjustedInsertPos = insertPos - imageNode.nodeSize;
        tr.insert(adjustedInsertPos, imageNode);
        view.dispatch(tr.scrollIntoView());
      } else {
        const pType = state.schema.nodes.paragraph;
        const tr = state.tr;
        tr.delete(imagePos, imagePos + imageNode.nodeSize);
        const newParagraph = pType.create(null, imageNode);
        tr.insert(tr.doc.content.size, newParagraph);
        view.dispatch(tr.scrollIntoView());
      }
    }
  };

  /**
   * تغییر سایز نرم و تدریجی با درگ گوشه‌ها
   */
  const handleResizeStart = (e, corner) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);

    const startX = e.clientX;
    const initialWidthPx = containerRef.current?.offsetWidth || 300;

    // محاسبه دقیق عرض برگه A4
    const pageEl =
      containerRef.current?.closest('.a4-sheet-wrapper') ||
      containerRef.current?.closest('.ProseMirror') ||
      document.body;
    const pageWidthPx = Math.max(300, (pageEl?.clientWidth || 700) - 40);

    let animationFrameId = null;

    const handleMouseMove = (moveEvent) => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);

      animationFrameId = requestAnimationFrame(() => {
        const deltaX = corner === 'right' ? moveEvent.clientX - startX : startX - moveEvent.clientX;
        const newWidthPx = Math.max(100, Math.min(pageWidthPx, initialWidthPx + deltaX));
        const percentage = Math.round((newWidthPx / pageWidthPx) * 100);
        const clamped = Math.max(20, Math.min(100, percentage));
        updateAttributes({ width: `${clamped}%` });
      });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // تنظیم استایل‌های چیدمان (راست، وسط، چپ)
  let wrapperStyle = {
    width: width || '50%',
    maxWidth: '100%',
    position: 'relative',
    userSelect: 'none',
  };

  let alignClass = '';
  if (align === 'right') {
    alignClass = 'float-right';
    wrapperStyle.float = 'right';
    wrapperStyle.margin = '4px 0 12px 16px';
    wrapperStyle.display = 'inline-block';
  } else if (align === 'left') {
    alignClass = 'float-left';
    wrapperStyle.float = 'left';
    wrapperStyle.margin = '4px 16px 12px 0';
    wrapperStyle.display = 'inline-block';
  } else {
    alignClass = 'block-center';
    wrapperStyle.display = 'block';
    wrapperStyle.margin = '16px auto';
    wrapperStyle.textAlign = 'center';
    wrapperStyle.clear = 'both';
    wrapperStyle.float = 'none';
  }

  return (
    <NodeViewWrapper
      as="span"
      data-drag-handle
      className={`relative group ${alignClass}`}
      style={wrapperStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div ref={containerRef} className="relative w-full" data-drag-handle>
        <img
          src={src}
          alt={alt || 'تصویر سوال'}
          data-drag-handle
          className={`w-full h-auto rounded-lg border transition-all cursor-grab active:cursor-grabbing ${
            selected || isResizing
              ? 'ring-2 ring-blue-500 border-blue-500 shadow-lg'
              : 'border-slate-200 hover:border-blue-300'
          }`}
          style={{ width: '100%', height: 'auto', display: 'block' }}
        />

        {/* دستگیره‌های تغییر اندازه گوشه‌ها */}
        {(selected || isHovered || isResizing) && (
          <>
            <div
              onMouseDown={(e) => handleResizeStart(e, 'right')}
              className="absolute -bottom-1.5 -right-1.5 w-3.5 h-3.5 bg-blue-600 border-2 border-white rounded-full shadow-md cursor-nwse-resize z-20 hover:scale-125 transition-transform"
              title="برای تغییر اندازه بکشید"
            />
            <div
              onMouseDown={(e) => handleResizeStart(e, 'left')}
              className="absolute -bottom-1.5 -left-1.5 w-3.5 h-3.5 bg-blue-600 border-2 border-white rounded-full shadow-md cursor-nesw-resize z-20 hover:scale-125 transition-transform"
              title="برای تغییر اندازه بکشید"
            />
          </>
        )}

        {/* نوار ابزار شناور تصویر */}
        {(selected || isHovered) && (
          <div
            className="absolute top-2 right-2 bg-white/95 backdrop-blur-sm border border-slate-200 shadow-xl rounded-xl p-1 flex items-center gap-1 z-30 text-xs"
            dir="rtl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* دستگیره درگ */}
            <div
              data-drag-handle
              title="برای جابه‌جایی در متن بکشید و رها کنید"
              className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900 cursor-grab active:cursor-grabbing"
            >
              <Move size={14} />
            </div>

            <span className="w-px h-4 bg-slate-200 mx-0.5" />

            {/* دکمه‌های جابه‌جایی بالا / پایین */}
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => moveImage('up')}
              title="انتقال به پاراگراف قبل"
              className="p-1.5 rounded-lg text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
            >
              <ArrowUp size={14} />
            </button>
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => moveImage('down')}
              title="انتقال به پاراگراف بعد"
              className="p-1.5 rounded-lg text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
            >
              <ArrowDown size={14} />
            </button>

            <span className="w-px h-4 bg-slate-200 mx-0.5" />

            {/* چینش تصویر (راست، وسط، چپ) */}
            <button
              type="button"
              onClick={() => updateAttributes({ align: 'right' })}
              title="شناور راست (تصویر در راست برگه، متن در چپ)"
              className={`p-1.5 rounded-lg transition-colors ${
                align === 'right' ? 'bg-blue-600 text-white shadow-xs' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <AlignRight size={14} />
            </button>
            <button
              type="button"
              onClick={() => updateAttributes({ align: 'center' })}
              title="وسط‌چین"
              className={`p-1.5 rounded-lg transition-colors ${
                align === 'center' ? 'bg-blue-600 text-white shadow-xs' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <AlignCenter size={14} />
            </button>
            <button
              type="button"
              onClick={() => updateAttributes({ align: 'left' })}
              title="شناور چپ (تصویر در چپ برگه، متن در راست)"
              className={`p-1.5 rounded-lg transition-colors ${
                align === 'left' ? 'bg-blue-600 text-white shadow-xs' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <AlignLeft size={14} />
            </button>

            <span className="w-px h-4 bg-slate-200 mx-0.5" />

            {/* پیش‌تنظیم‌های اندازه */}
            <button
              type="button"
              onClick={() => updateAttributes({ width: '30%' })}
              title="اندازه کوچک (۳۰٪)"
              className={`px-1.5 py-0.5 font-bold text-[11px] rounded-md transition-colors ${
                width === '30%' ? 'bg-blue-100 text-blue-700' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              ۳۰٪
            </button>
            <button
              type="button"
              onClick={() => updateAttributes({ width: '50%' })}
              title="اندازه متوسط (۵۰٪)"
              className={`px-1.5 py-0.5 font-bold text-[11px] rounded-md transition-colors ${
                width === '50%' ? 'bg-blue-100 text-blue-700' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              ۵۰٪
            </button>
            <button
              type="button"
              onClick={() => updateAttributes({ width: '100%' })}
              title="اندازه کامل (۱۰۰٪)"
              className={`px-1.5 py-0.5 font-bold text-[11px] rounded-md transition-colors ${
                width === '100%' ? 'bg-blue-100 text-blue-700' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              ۱۰۰٪
            </button>

            <span className="w-px h-4 bg-slate-200 mx-0.5" />

            {/* دکمه حذف */}
            <button
              type="button"
              onClick={() => deleteNode()}
              title="حذف تصویر"
              className="p-1.5 rounded-lg text-rose-600 hover:bg-rose-50 transition-colors"
            >
              <Trash2 size={14} />
            </button>
          </div>
        )}
      </div>
    </NodeViewWrapper>
  );
};

export default CustomImageNodeView;
